'use client';

import * as React from 'react';
import clsx from 'clsx';
import { InfoTooltip } from 'components/info-tooltip';
import { signInToDashboard } from 'components/sign-in-button';
import { TabButton, Tabs } from 'components/tabs';
import { useQueryParams } from 'hooks/use-query-params';
import { IconCheckCircle } from 'icon/General/check-circle';
import { IconXCircle } from 'icon/General/x-circle';
import { BlurFade, Button, FocusableLink, Separator } from 'ui';

export function Pricing(): JSX.Element {
  const [isAnnual, setIsAnnual] = React.useState(false);
  if (useQueryParams('ref') === 'iib') {
    return <></>;
  }
  return (
    <section
      className="mx-auto flex max-w-7xl flex-col items-center px-4 pt-16 xs:pt-24"
      id="pricing"
    >
      <div className="flex flex-col items-center text-center">
        <BlurFade delay={0.25}>
          <p className="pb-3 text-md font-semibold text-brand-700">Pricing</p>
        </BlurFade>
        <h2 className="text-balance pb-6 text-display-lg font-semibold text-gray-900">
          Simple, transparent pricing, no surprises
        </h2>
        {/* <BlurFade delay={0.25}>
          <DiscountNote />
        </BlurFade> */}
      </div>
      <div className="flex flex-col items-center gap-4">
        <BlurFade delay={0.25}>
          <Tabs className="mt-12">
            <TabButton
              active={!isAnnual}
              onClick={() => {
                setIsAnnual(false);
              }}
            >
              Monthly billing
            </TabButton>
            <TabButton
              active={isAnnual}
              onClick={() => {
                setIsAnnual(true);
              }}
            >
              Annual billing
              <span className={clsx('ms-2 hidden xs:inline', BADGE_STYLES)}>
                2 mth free
              </span>
            </TabButton>
          </Tabs>
        </BlurFade>
        <BlurFade delay={0.3}>
          <p className="text-sm font-medium text-gray-600 xs:hidden">
            Get 2 months free with annual billing
          </p>
        </BlurFade>
      </div>
      <div className="grid grid-cols-1 gap-8 pt-24 lg:grid-cols-2 xl:grid-cols-3">
        <PricingCard
          cta={{
            children: 'Try It Free',
            onClick: signInToDashboard,
          }}
          plan={{
            name: 'Free',
            price: 0,
            description:
              'Start your digital presence with essential blogging tools',
            features: [
              <>
                <IconCheckCircle className="text-brand-600" />
                Supports all Apple Notes features
              </>,
              <>
                <IconCheckCircle className="text-brand-600" />
                Supports markdown
              </>,
              <>
                <IconCheckCircle className="text-brand-600" />1 site & free
                subdomain
                <InfoTooltip
                  description={
                    <>
                      Get started with your own professional{' '}
                      {'<your-name>.quotion.co'} domain, claim yours today
                      before someone else does.
                    </>
                  }
                  title="Free domain"
                />
              </>,
              <>
                <IconCheckCircle className="text-brand-600" />
                Up to 5 notes{' '}
                <InfoTooltip
                  description="Perfect for getting started. Begin with up to 5 notes to experience the platform. Upgrade anytime to unlock unlimited publishing."
                  title="Note limit"
                />
              </>,
              <>
                <IconCheckCircle className="text-brand-600" />
                Basic personalization{' '}
                <InfoTooltip
                  description="Change the font, colors, toggle controls, and more. Upgrade to Lite+ plan to add CSS or JavaScript code when needed."
                  title="Site styles"
                />
              </>,
              <>
                <IconCheckCircle className="text-brand-600" />
                Personal use only{' '}
                <InfoTooltip
                  description="Free plan is for personal use only. All commercial usage requires a paid plan."
                  title="Commercial usage"
                />
              </>,
              <>
                <IconCheckCircle className="text-brand-600" />1 month analytics
                data retention
              </>,
              <>
                <IconXCircle className="text-gray-500" />
                No Quotion branding
              </>,
            ],
          }}
        />
        <PricingCard
          cta={{
            children: 'Get Started',
            onClick: signInToDashboard,
          }}
          isAnnual={isAnnual}
          plan={{
            name: 'Lite',
            price: isAnnual ? 120 : 12,
            originalPrice: isAnnual ? 160 : 16,
            description:
              'Scale your content with professional publishing tools',
            features: [
              <>
                <IconCheckCircle className="text-brand-600" />
                <span>Everything in Free plan</span>
              </>,
              <>
                <IconCheckCircle className="text-brand-600" />
                <span>Commercial use</span>
              </>,
              <>
                <IconCheckCircle className="text-brand-600" />
                <span>
                  Up to <span className="font-semibold text-brand-600">2</span>{' '}
                  sites & custom domains
                </span>
              </>,
              <>
                <IconCheckCircle className="text-brand-600" />
                <span>
                  <span className="font-semibold text-brand-600">
                    <span className="text-xl leading-none">∞ </span>
                    Unlimited
                  </span>{' '}
                  notes
                </span>
              </>,
              <>
                <IconCheckCircle className="text-brand-600" />
                <span>
                  Custom{' '}
                  <span className="font-semibold text-brand-600">CSS</span>
                </span>
              </>,
              <>
                <IconCheckCircle className="text-brand-600" />
                <span>
                  Supports{' '}
                  <span className="font-semibold text-brand-600">KaTeX</span>
                </span>
              </>,
              <>
                <IconCheckCircle className="text-brand-600" />
                <span>Export markdown</span>
              </>,
              <>
                <IconCheckCircle className="text-brand-600" />
                <span>
                  <span className="font-semibold text-brand-600">No </span>
                  Quotion branding
                </span>
              </>,
            ],
          }}
        />
        <PricingCard
          cta={{
            onClick: signInToDashboard,
          }}
          isAnnual={isAnnual}
          isPopular
          plan={{
            name: 'Pro',
            price: isAnnual ? 290 : 29,
            originalPrice: isAnnual ? 390 : 39,
            description:
              'Advanced features for growing businesses and creators',
            features: [
              <>
                <IconCheckCircle className="text-brand-600" />
                <span>Everything in Lite plan</span>
              </>,
              <>
                <IconCheckCircle className="text-brand-600" />
                <span>
                  Up to <span className="font-semibold text-brand-600">10</span>{' '}
                  sites & custom domains
                </span>
              </>,
              <>
                <IconCheckCircle className="shrink-0 text-brand-600" />
                <span className="font-semibold text-brand-600">
                  <span className="text-xl leading-none">∞ </span>
                  Unlimited newsletters
                </span>
                <InfoTooltip
                  description={
                    <>
                      Transform Apple Notes into engaging newsletters. Our
                      dashboard makes it simple to manage and send updates
                      directly to your audience with unlimited subscribers and
                      unlimited newsletters.{' '}
                      <FocusableLink
                        className="underline"
                        href="https://docs.quotion.co/features/newsletter"
                      >
                        Learn more
                      </FocusableLink>
                      .
                    </>
                  }
                  title="Unlimited newsletters"
                />
              </>,
              <>
                <IconCheckCircle className="text-brand-600" />
                <span>
                  Up to <span className="font-semibold text-brand-600">10</span>{' '}
                  team members per site
                </span>
              </>,
              <>
                <IconCheckCircle className="text-brand-600" />
                <span>
                  Host in {`domain's `}
                  <span className="font-semibold text-brand-600">
                    subdirectory
                  </span>
                </span>
                <InfoTooltip
                  description="Boost your SEO by hosting your blog directly on your domain (e.g., acme.com/blog). Benefit from domain authority and improved search rankings while maintaining a seamless user experience."
                  title="Enhanced SEO Benefits"
                />
              </>,
              <>
                <IconCheckCircle className="text-brand-600" />
                <span>
                  <span className="font-semibold text-brand-600">Webhook</span>
                  {' API'}
                </span>
                <InfoTooltip
                  description="Automate your workflow by connecting your site to your favorite tools. Get real-time notifications for content updates and integrate with your existing systems - all without managing complex infrastructure."
                  title="Webhook API"
                />
              </>,
              <>
                <IconCheckCircle className="text-brand-600" />
                <span>
                  <span className="font-semibold text-brand-600">
                    AI-generated
                  </span>
                  {' image caption'}
                </span>
                <InfoTooltip
                  description="Automatically generate accurate image captions in your site's primary language using Google Gemini 2.0 and OpenAI GPT-4o. Perfect for enhancing accessibility and user engagement!"
                  title="AI-generated image caption"
                />
              </>,
              <>
                <IconCheckCircle className="text-brand-600" />
                <span>
                  Custom{' '}
                  <span className="font-semibold text-brand-600">
                    JavaScript
                  </span>
                </span>
              </>,
              <>
                <IconCheckCircle className="text-brand-600" />
                <span>
                  <span className="font-semibold text-brand-600">1 year </span>
                  analytics data retention
                </span>
              </>,
              <>
                <IconCheckCircle className="text-brand-600" />
                <span className="font-semibold text-brand-600">Comments</span>
              </>,
              <>
                <IconCheckCircle className="text-brand-600" />
                <span>
                  <span className="font-semibold text-brand-600">Password</span>{' '}
                  protection
                </span>
              </>,
              <>
                <IconCheckCircle className="text-brand-600" />
                Live chat & email support
              </>,
            ],
          }}
        />
        <PricingCard
          cta={{
            onClick: signInToDashboard,
          }}
          isAnnual={isAnnual}
          plan={{
            name: 'Enterprise',
            price: isAnnual ? 2490 : 249,
            originalPrice: isAnnual ? 2990 : 299,
            description:
              'Tailored solutions for high-volume content publishers',
            features: [
              <>
                <IconCheckCircle className="text-brand-600" />
                <span>Everything in Pro plan</span>
              </>,
              <>
                <IconCheckCircle className="text-brand-600" />
                <span>
                  <span className="font-semibold text-brand-600">
                    ∞ Unlimited
                  </span>{' '}
                  sites & custom domains
                </span>
              </>,
              <>
                <IconCheckCircle className="text-brand-600" />
                <span>
                  <span className="font-semibold text-brand-600">
                    ∞ Unlimited
                  </span>{' '}
                  team members
                </span>
              </>,
              <>
                <IconCheckCircle className="text-brand-600" />
                <span>
                  <span className="font-semibold text-brand-600">
                    ∞ Life-long
                  </span>{' '}
                  analytics data retention
                </span>
              </>,
              <>
                <IconCheckCircle className="text-brand-600" />
                Priority live chat & email support
              </>,
            ],
          }}
        />
      </div>
    </section>
  );
}

type PricingCardProps = {
  plan: {
    name: string;
    price: number;
    originalPrice?: number;
    description: string;
    features: React.ReactNode[];
  };
  cta?: {
    children?: React.ReactNode;
    onClick?: () => void;
  };
  isPopular?: boolean;
  isAnnual?: boolean;
};
function PricingCard({ plan, cta, isPopular, isAnnual }: PricingCardProps) {
  return (
    <div className="rounded-2xl border bg-white shadow-lg md:min-w-[380px]">
      <div className="p-8">
        <BlurFade delay={0.1} className="flex justify-between">
          <h3 className="text-lg font-semibold text-gray-600">{plan.name}</h3>
          {isPopular && <span className={BADGE_STYLES}>Popular</span>}
        </BlurFade>
        <BlurFade>
          <p className="flex items-baseline gap-1 pt-4">
            <strong className="text-display-xl font-semibold text-gray-900">
              ${plan.price}
            </strong>
            {plan.originalPrice && (
              <span className="text-display-md text-gray-600 line-through">
                ${plan.originalPrice}
              </span>
            )}
            <span className="text-md font-medium text-gray-600">
              {isAnnual ? '/ year' : '/ month'}
            </span>
          </p>
        </BlurFade>
        <BlurFade delay={0.2}>
          <p className="h-16 pt-4 text-md text-gray-600">{plan.description}</p>
        </BlurFade>
        <BlurFade delay={0.2} className="pt-8">
          <Button className="w-full" onClick={cta?.onClick}>
            {cta?.children || 'Get Started'}
          </Button>
        </BlurFade>
      </div>
      <Separator />
      <div className="p-8">
        <BlurFade delay={0.25}>
          <h4 className="text-md font-semibold text-gray-900">{`WHAT'S INCLUDED`}</h4>
        </BlurFade>
        <ul className="space-y-4 pt-6 text-start">
          {plan.features.map((feature, idx) => (
            <BlurFade delay={0.25 + idx * 0.05} key={idx}>
              <li className="flex items-start gap-3 text-md text-gray-600">
                {feature}
              </li>
            </BlurFade>
          ))}
        </ul>
      </div>
    </div>
  );
}

export const BADGE_STYLES = `rounded-full border border-brand-200 bg-brand-50 px-1.5 py-1 text-xs font-medium text-brand-700`;
